import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, UserPlus, Users as UsersIcon, Loader2, AlertCircle,
  ShieldCheck, X, Check, Ban,
} from 'lucide-react';
import api from '@utils/api';
import { ROLES, PLATFORMS } from '@config/platforms';
import { StationAccess } from '@components/admin/StationAccess';
import { AreaAccess } from '@components/admin/AreaAccess';
import { StationChips, AreaChips } from '@components/admin/StationChips';
import {
  modeAfterRoleChange, modeOf, areaModeOf, toPayload, validateDraft, canSeeStation, canSeeArea, missingScope,
} from '@utils/stationScope';

const roleLabel = (r) => r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Role dropdown options (value → readable label).
const ROLE_OPTIONS = Object.values(ROLES).map((r) => ({ value: r, label: roleLabel(r) }));

// Platforms a super-admin can grant. 'dashboard' = Planning (GroundOptimizer).
const PLATFORM_OPTIONS = PLATFORMS.map((p) => ({ id: p.id, title: p.title }));

const EMPTY = { name: '', email: '', role: 'staff', platform_access: [], password: '', mode: 'none', home: '', scope: [], area: 'both' };

function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl p-6 ${className}`}
      style={{
        background:     'rgba(10,16,35,0.9)',
        border:         '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(24px)',
        boxShadow:      '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(45,125,70,0.06)',
      }}
    >
      {children}
    </div>
  );
}

function UserModal({ initial, stations, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(
    isEdit
      ? {
          ...EMPTY, ...initial,
          platform_access: initial.platform_access || [],
          password: '',
          mode: modeOf(initial),
          home: initial.home_station || '',
          scope: initial.station_scope || [],
          area: areaModeOf(initial),
        }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);
  const nameRef = useRef(null);
  const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const savingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  savingRef.current = saving;
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = openerRef.current;
    nameRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !savingRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const items = [...dialogRef.current.querySelectorAll(
      'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])',
    )].filter((el) => !el.disabled && el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  const togglePlatform = (id) =>
    setForm((f) => ({
      ...f,
      platform_access: f.platform_access.includes(id)
        ? f.platform_access.filter((p) => p !== id)
        : [...f.platform_access, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    const invalid = validateDraft(form);
    if (invalid) { setError(invalid); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const patch = {
          name: form.name,
          role: form.role,
          platform_access: form.platform_access,
          ...toPayload(form),
        };
        if (form.password) patch.password = form.password;
        const { data } = await api.patch(`/users/${initial.id}`, patch);
        onSaved(data);
      } else {
        const { data } = await api.post('/users', {
          name: form.name,
          email: form.email,
          role: form.role,
          platform_access: form.platform_access,
          password: form.password,
          ...toPayload(form),
        });
        onSaved(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save user');
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg text-sm text-gl-ghost placeholder-gl-muted bg-gl-surface border border-gl-border focus:outline-none focus-visible:ring-2 focus-visible:ring-gl-ghost';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <motion.div
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 40, opacity: 0 }}
        className="w-full max-w-md h-full overflow-y-auto"
        ref={dialogRef}
        onKeyDown={trapTab}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit user' : 'New user'}
      >
        <Card className="space-y-4 !rounded-none min-h-full">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-display font-semibold text-gl-ghost">
              {isEdit ? 'Edit user' : 'New user'}
            </h2>
            <button type="button" onClick={onClose} aria-label="Close" className="text-gl-muted hover:text-gl-ghost focus-visible:ring-2 focus-visible:ring-gl-ghost rounded"><X size={16} /></button>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
              <AlertCircle size={12} className="shrink-0" />{error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="user-name" className="text-2xs font-mono uppercase tracking-widest text-gl-muted">Full name</label>
                <input id="user-name" ref={nameRef} className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <label htmlFor="user-email" className="text-2xs font-mono uppercase tracking-widest text-gl-muted">Email</label>
                <input id="user-email" className={inputCls} type="email" value={form.email} disabled={isEdit}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
            </div>

            <div>
              <label htmlFor="user-role" className="text-2xs font-mono uppercase tracking-widest text-gl-muted">Role</label>
              <select id="user-role" className={inputCls} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value, mode: modeAfterRoleChange(f.role, e.target.value, f.mode) }))}>
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <span id="user-platform-label" className="text-2xs font-mono uppercase tracking-widest text-gl-muted">Platform access</span>
              <div role="group" aria-labelledby="user-platform-label" className="grid grid-cols-2 gap-2 mt-1">
                {PLATFORM_OPTIONS.map((p) => {
                  const on = form.platform_access.includes(p.id);
                  return (
                    <button type="button" key={p.id} onClick={() => togglePlatform(p.id)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all"
                      style={on
                        ? { background: 'rgba(45,125,70,0.12)', border: '1px solid rgba(45,125,70,0.35)', color: '#2D7D46' }
                        : { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', color: '#8199B4' }}>
                      {on ? <Check size={12} /> : <span className="w-3" />}
                      {p.title}
                    </button>
                  );
                })}
              </div>
            </div>

            <StationAccess form={form} setForm={setForm} stations={stations} />

            <AreaAccess form={form} setForm={setForm} />

            <div>
              <label htmlFor="user-password" className="text-2xs font-mono uppercase tracking-widest text-gl-muted">
                {isEdit ? 'Reset password (optional)' : 'Initial password'}
              </label>
              <input id="user-password" className={inputCls} type="password" value={form.password} minLength={12}
                placeholder="At least 12 characters"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!isEdit} />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button type="submit" disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-2xs font-mono uppercase tracking-wider"
                style={{ background: 'linear-gradient(135deg, #2D7D46, #1A5E9E)', color: '#0D1B2A' }}>
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {isEdit ? 'Save changes' : 'Create user'}
              </button>
              <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-2xs font-mono uppercase tracking-wider text-gl-muted">
                Cancel
              </button>
            </div>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}

export function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {} for new, {…} for edit, null closed
  const [roleFilter, setRoleFilter] = useState('all');
  const [grouped, setGrouped] = useState(true);
  const [stations, setStations] = useState([]);
  const [stationFilter, setStationFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/users')
      .then(({ data }) => setUsers(data))
      .catch((err) => setError(err.response?.data?.message || 'Could not load users'))
      .finally(() => setLoading(false));
    api.get('/stations')
      .then(({ data }) => setStations(data.stations || []))
      .catch(() => setStations([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only roles actually present on real accounts - not every possible role,
  // so the filter dropdown doesn't list roles nobody holds yet.
  const rolesInUse = [...new Set(users.map((u) => u.role))].sort((a, b) => roleLabel(a).localeCompare(roleLabel(b)));
  const missingCount = missingScope(users).length;
  const filtered = users
    .filter((u) => roleFilter === 'all' || u.role === roleFilter)
    .filter((u) => stationFilter === 'all' || canSeeStation(u, stationFilter))
    .filter((u) => areaFilter === 'all' || canSeeArea(u, areaFilter))
    .filter((u) => !onlyMissing || missingScope([u]).length === 1);
  const groups = grouped
    ? Object.entries(
        filtered.reduce((acc, u) => { (acc[u.role] ||= []).push(u); return acc; }, {}),
      ).sort(([a], [b]) => roleLabel(a).localeCompare(roleLabel(b)))
    : [[null, filtered]];

  const onSaved = (saved) => {
    setUsers((list) => {
      const idx = list.findIndex((u) => u.id === saved.id);
      if (idx === -1) return [...list, saved].sort((a, b) => a.name.localeCompare(b.name));
      const copy = [...list]; copy[idx] = { ...copy[idx], ...saved }; return copy;
    });
    setEditing(null);
  };

  const toggleActive = async (u) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      onSaved({ ...u, ...data });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update user');
    }
  };

  return (
    <div className="min-h-screen bg-gl-void px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-gl-muted hover:text-gl-ghost mb-6 transition-colors">
          <ArrowLeft size={12} /> Back to hub
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-display font-bold text-gl-ghost">
              <ShieldCheck size={22} style={{ color: '#2D7D46' }} /> User Management
            </h1>
            <p className="text-xs text-gl-muted mt-1">Create accounts and grant access to HR, Planning and other platforms.</p>
          </div>
          <button onClick={() => setEditing({})}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-2xs font-mono uppercase tracking-wider"
            style={{ background: 'linear-gradient(135deg, #2D7D46, #1A5E9E)', color: '#0D1B2A' }}>
            <UserPlus size={13} /> New user
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
            <AlertCircle size={12} className="shrink-0" />{error}
          </div>
        )}

        {!loading && users.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select
              className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider bg-gl-surface border border-gl-border text-gl-ghost focus:outline-none focus-visible:ring-2 focus-visible:ring-gl-ghost"
              value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All roles ({users.length})</option>
              {rolesInUse.map((r) => (
                <option key={r} value={r}>{roleLabel(r)} ({users.filter((u) => u.role === r).length})</option>
              ))}
            </select>
            <button type="button" onClick={() => setGrouped((g) => !g)}
              className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider"
              style={grouped
                ? { background: 'rgba(45,125,70,0.12)', border: '1px solid rgba(45,125,70,0.35)', color: '#2D7D46' }
                : { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', color: '#8199B4' }}>
              Group by role
            </button>
            <select
              aria-label="Filter by station"
              className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider bg-gl-surface border border-gl-border text-gl-ghost focus:outline-none focus-visible:ring-2 focus-visible:ring-gl-ghost"
              value={stationFilter} onChange={(e) => setStationFilter(e.target.value)}>
              <option value="all">All stations</option>
              {stations.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.name}</option>)}
            </select>
            <select
              aria-label="Filter by department"
              className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider bg-gl-surface border border-gl-border text-gl-ghost focus:outline-none focus-visible:ring-2 focus-visible:ring-gl-ghost"
              value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
              <option value="all">All departments</option>
              <option value="BOH">BOH</option>
              <option value="FOH">FOH</option>
            </select>
            <button type="button" onClick={() => setOnlyMissing((v) => !v)} aria-pressed={onlyMissing}
              className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider"
              style={onlyMissing
                ? { background: '#E8A000', border: '1px solid #E8A000', color: '#0D1B2A' }
                : { background: 'rgba(232,160,0,0.08)', border: '1px dashed #E8A000', color: '#E8A000' }}>
              Scope missing · {missingCount}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gl-muted"><Loader2 size={18} className="animate-spin" /></div>
        ) : users.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-16 text-center">
            <UsersIcon size={28} className="text-gl-muted mb-3" />
            <p className="text-sm text-gl-ghost">No users yet</p>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-16 text-center">
            <UsersIcon size={28} className="text-gl-muted mb-3" />
            <p className="text-sm text-gl-ghost">No users with this role</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {groups.map(([role, list]) => (
              <div key={role ?? 'all'}>
                {role !== null && (
                  <p className="text-2xs font-mono uppercase tracking-widest text-gl-muted mb-1.5 px-1">
                    {roleLabel(role)} ({list.length})
                  </p>
                )}
                <Card className="space-y-1">
                  {list.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/[0.03] transition-colors">
                      <div className="min-w-0">
                        <p className="text-sm text-gl-ghost truncate">
                          {u.name}
                          {!u.is_active && <span className="ml-2 text-2xs font-mono uppercase text-gl-muted">(inactive)</span>}
                        </p>
                        <p className="text-2xs text-gl-muted font-mono truncate">{u.email} · {roleLabel(u.role)}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(u.platform_access || []).map((p) => (
                            <span key={p} className="text-2xs font-mono px-1.5 py-0.5 rounded"
                              style={{ background: 'rgba(45,125,70,0.1)', color: '#2D7D46' }}>{p}</span>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1" data-testid="user-stations">
                          <StationChips user={u} />
                          <AreaChips user={u} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => toggleActive(u)} title={u.is_active ? 'Deactivate' : 'Activate'}
                          className="p-2 rounded-lg text-gl-muted hover:text-gl-ghost">
                          {u.is_active ? <Ban size={14} /> : <Check size={14} />}
                        </button>
                        <button onClick={() => setEditing(u)}
                          className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider"
                          style={{ background: 'rgba(45,125,70,0.1)', border: '1px solid rgba(45,125,70,0.25)', color: '#2D7D46' }}>
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {editing !== null && (
          <UserModal initial={editing} stations={stations} onClose={() => setEditing(null)} onSaved={onSaved} />
        )}
      </AnimatePresence>
    </div>
  );
}
